import React from 'react'
import { Link } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import {
  TrendingUp, Home, FileText, AlertCircle,
  Plus, ArrowRight, LayoutGrid, RotateCcw, Save,
} from 'lucide-react'
import { AppLayout } from '../components/layout/AppLayout'
import { Card } from '../components/ui/Card'
import { Chip } from '../components/ui/Chip'
import { Button } from '../components/ui/Button'
import type { KpiCardProps } from '../components/dashboard/KpiCard'
import { DraggableKpiStrip } from '../components/dashboard/DraggableKpiStrip'
import { DraggableWidgetGrid } from '../components/dashboard/DraggableWidgetGrid'
import { WidgetKommende } from '../components/dashboard/WidgetKommende'
import { WidgetTomgang } from '../components/dashboard/WidgetTomgang'
import { WidgetOpkraevning } from '../components/dashboard/WidgetOpkraevning'
import { WidgetSelskaber } from '../components/dashboard/WidgetSelskaber'
import type { CompanyKpisResponse } from '../components/dashboard/WidgetSelskaber'
import { apiFetch } from '../lib/api-client'

// ─── Types ───────────────────────────────────────────────────────────────────

interface Lease {
  id: string
  property_id: string
  unit_no: string
  type: string
  area_m2: number | null
}

interface Contract {
  id: string
  lease_id: string
  monthly_rent_oere: number
  status: string
  start_date: string
  end_date: string | null
}

interface BillingSummary {
  leje_oere: number
  forbrug_oere: number
  ovrige_oere: number
  total_oere: number
}

interface UpcomingItem {
  label: string
  sub: string
  color: string
}

// ─── localStorage helpers ────────────────────────────────────────────────────

const LAYOUT_KEY = 'boligflow_dashboard_layout'

const DEFAULT_KPI_ORDER = ['leases', 'rent', 'occupancy', 'arrears']
const DEFAULT_WIDGET_ORDER = ['upcoming', 'vacancy', 'billing_split', 'leases_table', 'companies']

function loadLayout(): { kpiOrder: string[]; widgetOrder: string[] } {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY)
    if (!raw) return { kpiOrder: [...DEFAULT_KPI_ORDER], widgetOrder: [...DEFAULT_WIDGET_ORDER] }
    const parsed = JSON.parse(raw)
    const validKpi = new Set(DEFAULT_KPI_ORDER)
    const validWidget = new Set(DEFAULT_WIDGET_ORDER)
    const kpiFiltered = Array.isArray(parsed.kpiOrder)
      ? parsed.kpiOrder.filter((id: unknown) => typeof id === 'string' && validKpi.has(id as string))
      : [...DEFAULT_KPI_ORDER]
    const widgetFiltered = Array.isArray(parsed.widgetOrder)
      ? parsed.widgetOrder.filter((id: unknown) => typeof id === 'string' && validWidget.has(id as string))
      : [...DEFAULT_WIDGET_ORDER]
    return {
      kpiOrder: [...kpiFiltered, ...DEFAULT_KPI_ORDER.filter(id => !kpiFiltered.includes(id))],
      widgetOrder: [...widgetFiltered, ...DEFAULT_WIDGET_ORDER.filter(id => !widgetFiltered.includes(id))],
    }
  } catch {
    return { kpiOrder: [...DEFAULT_KPI_ORDER], widgetOrder: [...DEFAULT_WIDGET_ORDER] }
  }
}

function saveLayout(kpiOrder: string[], widgetOrder: string[]) {
  localStorage.setItem(LAYOUT_KEY, JSON.stringify({ kpiOrder, widgetOrder }))
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDKK(oere: number): string {
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(oere / 100)
}

function vacantSinceLabel(endDateIso: string): string {
  return new Date(endDateIso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

function daysSince(endDateIso: string): number {
  const ms = Date.now() - new Date(endDateIso).getTime()
  return Math.max(0, Math.floor(ms / 86_400_000))
}

function contractChipVariant(status: string): 'green' | 'blue' | 'amber' | 'gray' | 'purple' | 'red' {
  switch (status) {
    case 'active': return 'green'
    case 'signed': return 'blue'
    case 'sent': return 'purple'
    case 'draft': return 'gray'
    case 'terminated': return 'red'
    default: return 'gray'
  }
}

function contractStatusLabel(status: string): string {
  switch (status) {
    case 'active': return 'Aktiv'
    case 'signed': return 'Underskrevet'
    case 'sent': return 'Sendt'
    case 'draft': return 'Kladde'
    case 'terminated': return 'Opsagt'
    default: return status
  }
}

// ─── Dashboard Page ───────────────────────────────────────────────────────────

export function DashboardPage() {
  const [leases, setLeases] = React.useState<Lease[]>([])
  const [contracts, setContracts] = React.useState<Contract[]>([])
  const [billingSummary, setBillingSummary] = React.useState<BillingSummary | null>(null)
  const [overdueCount, setOverdueCount] = React.useState<number | null>(null)
  const [upcomingItems, setUpcomingItems] = React.useState<UpcomingItem[]>([])
  const [companyKpis, setCompanyKpis] = React.useState<CompanyKpisResponse | null>(null)
  const [loading, setLoading] = React.useState(true)

  const [editMode, setEditMode] = React.useState(false)
  const [kpiOrder, setKpiOrder] = React.useState<string[]>(() => loadLayout().kpiOrder)
  const [widgetOrder, setWidgetOrder] = React.useState<string[]>(() => loadLayout().widgetOrder)

  const kpiSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const widgetSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  React.useEffect(() => {
    let alive = true
    Promise.all([
      apiFetch<Lease[]>('/api/v1/leases'),
      apiFetch<Contract[]>('/api/v1/contracts'),
      apiFetch<BillingSummary>('/api/v1/financial/billing-summary'),
      apiFetch<{ items: unknown[]; total?: number } | unknown[]>('/api/v1/rent-payments?status=overdue').catch(() => null),
      apiFetch<{ items: UpcomingItem[] }>('/api/v1/dashboard/upcoming').catch(() => null),
      apiFetch<CompanyKpisResponse>('/api/v1/dashboard/kpis').catch(() => null),
    ]).then(([l, c, bs, overdueResp, upcomingResp, kpisResp]) => {
      if (!alive) return
      if (kpisResp && Array.isArray(kpisResp.companies)) setCompanyKpis(kpisResp)
      setLeases(l as Lease[])
      setContracts(c as Contract[])
      setBillingSummary(bs as BillingSummary)
      if (overdueResp !== null) {
        const resp = overdueResp as { items?: unknown[]; total?: number } | unknown[]
        if (Array.isArray(resp)) {
          setOverdueCount(resp.length)
        } else if (typeof resp === 'object' && resp !== null) {
          const r = resp as { items?: unknown[]; total?: number }
          setOverdueCount(r.total ?? r.items?.length ?? 0)
        }
      }
      if (upcomingResp && Array.isArray(upcomingResp.items)) {
        setUpcomingItems(
          upcomingResp.items.map((it) => ({ label: it.label, sub: it.sub, color: it.color })),
        )
      }
    }).catch(() => {
      // keep empty state — widgets show "Ingen data" gracefully
    }).finally(() => {
      if (alive) setLoading(false)
    })
    return () => { alive = false }
  }, [])

  // ─── Computed values ───────────────────────────────────────────────────────

  const activeContracts = contracts.filter((c) => c.status === 'active' || c.status === 'signed' || c.status === 'sent')
  const totalRentOere = activeContracts.reduce((sum, c) => sum + c.monthly_rent_oere, 0)
  const occupiedLeaseIds = new Set(activeContracts.map((c) => c.lease_id))
  const occupancyPct = leases.length > 0 ? Math.round((occupiedLeaseIds.size / leases.length) * 100) : 0

  const vacantLeases = leases
    .filter((l) => !occupiedLeaseIds.has(l.id))
    .map((l) => {
      // Most recent ended contract = end of the previous tenancy → "ledig siden".
      // A vacant lease has no active/signed/sent contract, so all its contracts are non-active.
      const lastEnded = contracts
        .filter((c) => c.lease_id === l.id && c.end_date)
        .sort((a, b) => (b.end_date as string).localeCompare(a.end_date as string))[0]
      const endDate = lastEnded?.end_date ?? null
      return {
        unitNo: l.unit_no,
        vacantSince: endDate ? vacantSinceLabel(endDate) : '—',
        vacantSinceDays: endDate ? daysSince(endDate) : 0,
      }
    })

  const latestContractByLease = new Map<string, Contract>()
  for (const c of contracts) {
    const existing = latestContractByLease.get(c.lease_id)
    if (!existing || c.start_date > existing.start_date) {
      latestContractByLease.set(c.lease_id, c)
    }
  }
  const tableRows = leases.map((lease) => ({
    lease,
    contract: latestContractByLease.get(lease.id) ?? null,
  }))

  // ─── KPI definitions ───────────────────────────────────────────────────────

  const kpiDefs: Record<string, KpiCardProps> = {
    leases: {
      id: 'leases',
      label: 'Lejemål i alt',
      value: loading ? '…' : String(leases.length),
      sub: `${leases.length} enheder`,
      icon: <Home size={18} />,
      accent: '#4f46e5',
      loading,
    },
    rent: {
      id: 'rent',
      label: 'Månedlig leje',
      value: loading ? '…' : formatDKK(totalRentOere),
      sub: `${activeContracts.length} aktive kontrakter`,
      icon: <TrendingUp size={18} />,
      accent: '#16a34a',
      loading,
    },
    occupancy: {
      id: 'occupancy',
      label: 'Belægning',
      value: loading ? '…' : `${occupancyPct} %`,
      sub: `${occupiedLeaseIds.size} af ${leases.length} lejemål`,
      icon: <FileText size={18} />,
      accent: '#0284c7',
      loading,
    },
    arrears: {
      id: 'arrears',
      label: 'Restancer',
      value: loading ? '…' : overdueCount !== null ? String(overdueCount) : '—',
      sub: overdueCount !== null ? (overdueCount === 0 ? 'Ingen forfaldne betalinger' : `${overdueCount} forfaldne betalinger`) : 'Henter…',
      icon: <AlertCircle size={18} />,
      accent: '#d97706',
      loading,
    },
  }

  // ─── Drag handlers ─────────────────────────────────────────────────────────

  function handleKpiDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setKpiOrder((prev) => {
        const oldIndex = prev.indexOf(String(active.id))
        const newIndex = prev.indexOf(String(over.id))
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }

  function handleWidgetDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setWidgetOrder((prev) => {
        const oldIndex = prev.indexOf(String(active.id))
        const newIndex = prev.indexOf(String(over.id))
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }

  function handleSave() {
    saveLayout(kpiOrder, widgetOrder)
    setEditMode(false)
  }

  function handleReset() {
    setKpiOrder(DEFAULT_KPI_ORDER)
    setWidgetOrder(DEFAULT_WIDGET_ORDER)
    localStorage.removeItem(LAYOUT_KEY)
    setEditMode(false)
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  const kpiItems = kpiOrder.map((id) => kpiDefs[id]).filter(Boolean) as KpiCardProps[]
  const widgetItems = widgetOrder.map((id) => {
    const node = makeWidgetNode(id, { loading, editMode, upcomingItems, vacantLeases, billingSummary, tableRows, companyKpis })
    return node ? { id, node } : null
  }).filter(Boolean) as { id: string; node: React.ReactNode }[]

  return (
    <AppLayout
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {editMode ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleReset}
              >
                <RotateCcw size={13} style={{ marginRight: 4 }} />
                Nulstil
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Save size={13} style={{ marginRight: 4 }} />
                Gem layout
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditMode(true)}
              >
                <LayoutGrid size={13} style={{ marginRight: 4 }} />
                Tilpas dashboard
              </Button>
              <Link to="/lejemaal/opret">
                <Button size="sm">
                  <Plus size={13} style={{ marginRight: 4 }} />
                  Nyt lejemål
                </Button>
              </Link>
            </>
          )}
        </div>
      }
    >
      {editMode && (
        <div style={{
          marginBottom: 16, padding: '8px 14px',
          background: '#eff6ff', border: '1px solid #dbeafe',
          borderRadius: 8, fontSize: 12, color: '#4f46e5',
        }}>
          Tilpasningstilstand aktiv — træk kort og widgets for at omarrangere
        </div>
      )}

      {/* KPI Strip */}
      <DndContext sensors={kpiSensors} collisionDetection={closestCenter} onDragEnd={handleKpiDragEnd}>
        <DraggableKpiStrip items={kpiItems} editMode={editMode} />
      </DndContext>

      {/* Widget Grid */}
      <DndContext sensors={widgetSensors} collisionDetection={closestCenter} onDragEnd={handleWidgetDragEnd}>
        <DraggableWidgetGrid items={widgetItems} editMode={editMode} />
      </DndContext>
    </AppLayout>
  )
}

// ─── Widget factory ────────────────────────────────────────────────────────────

interface WidgetFactoryArgs {
  loading: boolean
  editMode: boolean
  upcomingItems: UpcomingItem[]
  vacantLeases: { unitNo: string; vacantSinceDays: number; vacantSince: string }[]
  billingSummary: { leje_oere: number; forbrug_oere: number; ovrige_oere: number; total_oere: number } | null
  tableRows: { lease: Lease; contract: Contract | null }[]
  companyKpis: CompanyKpisResponse | null
}

function makeWidgetNode(id: string, args: WidgetFactoryArgs): React.ReactNode | null {
  const { loading, editMode, upcomingItems, vacantLeases, billingSummary, tableRows, companyKpis } = args
  switch (id) {
    case 'upcoming':
      return <WidgetKommende items={upcomingItems} loading={loading} editMode={editMode} />
    case 'vacancy':
      return <WidgetTomgang vacantLeases={vacantLeases} loading={loading} editMode={editMode} />
    case 'billing_split':
      return <WidgetOpkraevning summary={billingSummary} loading={loading} editMode={editMode} />
    case 'leases_table':
      return <LejesforholdsWidget tableRows={tableRows} loading={loading} editMode={editMode} />
    case 'companies':
      // Only relevant for landlords with 2+ companies
      if (!companyKpis || companyKpis.companies.length < 2) return null
      return <WidgetSelskaber data={companyKpis} loading={loading} editMode={editMode} />
    default:
      return null
  }
}

// ─── Lejeforhold widget ───────────────────────────────────────────────────────

interface LejeforholdsWidgetProps {
  tableRows: { lease: Lease; contract: Contract | null }[]
  loading: boolean
  editMode?: boolean
}

function LejesforholdsWidget({ tableRows, loading, editMode }: LejeforholdsWidgetProps) {
  return (
    <Card
      padding="none"
      style={editMode ? { border: '1.5px dashed #4f46e5', background: '#fafbff' } : undefined}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px 8px', borderBottom: '1px solid #f3f4f6',
      }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Lejeforhold</span>
        <Link to="/lejeforhold" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#4f46e5', textDecoration: 'none' }}>
          Se alle <ArrowRight size={12} />
        </Link>
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Lejemål', 'Type', 'Månedsleje', 'Status', ''].map((h) => (
                <th key={h} style={{
                  padding: '8px 16px', textAlign: 'left', fontSize: 11,
                  fontWeight: 600, color: '#6b7280', textTransform: 'uppercase',
                  letterSpacing: '0.04em', borderBottom: '1px solid #e8eaed',
                  whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.slice(0, 5).map(({ lease, contract }, i) => (
              <LejeforholdsRow
                key={lease.id}
                lease={lease}
                contract={contract}
                isLast={i === Math.min(tableRows.length, 5) - 1}
              />
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

function LejeforholdsRow({ lease, contract, isLast }: { lease: Lease; contract: Contract | null; isLast: boolean }) {
  const [hovered, setHovered] = React.useState(false)
  const typeLabel: Record<string, string> = {
    residential: 'Lejlighed', commercial: 'Erhverv', garage: 'Garage', storage: 'Depot',
  }
  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? '#f9fafb' : 'transparent',
        transition: 'background 0.1s',
        borderBottom: isLast ? 'none' : '1px solid #e8eaed',
      }}
    >
      <td style={{ padding: '10px 16px', fontWeight: 500, color: '#111827' }}>{lease.unit_no}</td>
      <td style={{ padding: '10px 16px', color: '#6b7280' }}>{typeLabel[lease.type] ?? lease.type}</td>
      <td style={{ padding: '10px 16px', color: '#111827', fontWeight: 500 }}>
        {contract ? formatDKK(contract.monthly_rent_oere) : <span style={{ color: '#9ca3af' }}>—</span>}
      </td>
      <td style={{ padding: '10px 16px' }}>
        {contract ? (
          <Chip variant={contractChipVariant(contract.status)}>{contractStatusLabel(contract.status)}</Chip>
        ) : (
          <Chip variant="gray">Ingen kontrakt</Chip>
        )}
      </td>
      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
        <Link to={`/lejemaal/${lease.id}`} style={{ fontSize: 12, color: '#4f46e5', textDecoration: 'none' }}>
          <ArrowRight size={14} />
        </Link>
      </td>
    </tr>
  )
}
